package io.horion.cv.viewModel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.cv.api.datasource.payement.PaymentMethod
import io.horion.cv.api.datasource.response.PaymentStatus
import io.horion.cv.api.usecase.CvUseCase
import io.horion.cv.api.usecase.PayUseCase
import io.horion.cv.api.utils.LocalStorageManager
import io.horion.cv.uiState.CvUiState
import io.horion.cv.uiState.PaymentUiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.seconds

class PayViewModel(
    private val payUseCase: PayUseCase,
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main)
) {
    var paymentUiState by mutableStateOf(PaymentUiState())
        private set

    fun dismissError() {
        paymentUiState = paymentUiState.copy(error = null)
    }

    fun initPayment() {
        scope.launch {
            paymentUiState = paymentUiState.copy(
                paymentSuccessLancer = false,
                paymentSuccess = false,
                error = null,
                loading = true
            )

            val sessionUuid = LocalStorageManager.getSessionUuid()
            if (sessionUuid == null) {
                paymentUiState = paymentUiState.copy(
                    loading = false,
                    error = "Session invalide, veuillez vous rebrancher."
                )
                return@launch
            }

            try {
                val response = payUseCase.payement(
                    sessionsUuid = sessionUuid,
                    amount = paymentUiState.amount,
                    phoneNumber = paymentUiState.phone,
                    method = paymentUiState.method,
                )

                paymentUiState = if (response.success) {
                    paymentUiState.copy(
                        paymentSuccessLancer = true,
                        error = null,
                        loading = false
                    )
                } else {
                    paymentUiState.copy(
                        paymentSuccessLancer = false,
                        error = response.message.ifBlank { "Une erreur s'est produite lors du paiement" },
                        loading = false
                    )
                }

            } catch (e: Exception) {
                paymentUiState = paymentUiState.copy(
                    paymentSuccessLancer = false,
                    loading = false,
                    error = e.message ?: "Une erreur inattendue est survenue"
                )
            }
        }
    }

    suspend fun pollCvStatus(
        maxAttempts: Int = 30,
        delayMillis: Long = 3
    ) {
        val sessionUuid = LocalStorageManager.getSessionUuid() ?: return
        paymentUiState = paymentUiState.copy(loading = true, paymentSuccess = false)
        repeat(maxAttempts) { attempt ->

            val res = payUseCase.pollCvStatus(sessionUuid)

            when {
                res == null -> {
                    paymentUiState = paymentUiState.copy(
                        paymentSuccess = false,
                        loading = false
                    )
                    return
                }

                res.status == PaymentStatus.SUCCESS  -> {
                    paymentUiState = paymentUiState.copy(
                        paymentSuccess = true,
                        downloadUrl = res.message,
                        error = null,
                        loading = false
                    )
                    return
                }
                res.status == PaymentStatus.FAILED -> {
                    paymentUiState = paymentUiState.copy(
                        paymentSuccess = false,
                        loading = false,
                        error = res.message
                    )
                    return
                }

                else -> {
                    // Si ce n'est pas la dernière tentative, attendre
                    if (attempt < maxAttempts - 1) {
                        delay(delayMillis.seconds)
                    }
                }
            }
        }

        // Timeout après maxAttempts
        paymentUiState = paymentUiState.copy(
            paymentSuccess = false,
            error = "Temps d'attente ecouler veiller annuller la transaction de votre cote",
            loading = false
        )
    }

    fun downloadCv(){
        scope.launch {
            payUseCase.downloadCv(paymentUiState.downloadUrl)
        }
    }

    fun updatePhone(phone : String){
        paymentUiState = paymentUiState.copy(
            phone = phone
        )
    }

    fun updateMethod(method : PaymentMethod){
        paymentUiState = paymentUiState.copy(
            method = method
        )
    }

    fun cleanError(){
        paymentUiState = paymentUiState.copy(
            error = null,
            paymentSuccess = false,
            paymentSuccessLancer = false
        )
    }
}